'use client';

import { Button, type ButtonProps } from '@/app/components/button';
import { ButtonGroup } from '@/app/components/button-group';
import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { Image } from '@/app/components/media/image';
import { generateSources } from '@/app/components/media/image/generate-sources';
import type { ImageProps } from '@/app/components/media/image/types';
import styles from '@/app/components/sections/service/service-center/style.module.css';
import { useRevealAnim } from '@/app/hooks/useRevealAnim';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA, type DataGAProps } from '@/app/utils/createDataGA';

type FeatureItemType = {
  title?: string;
  description?: string;
  icon?: Pick<ImageProps, 'src' | 'alt'>;
};

type ServiceCenterFeatures = {
  title?: string;
  description?: string;
  imageDesktop?: Omit<ImageProps, 'aspectRatio' | 'alt'>;
  imageMobile?: Omit<ImageProps, 'aspectRatio' | 'alt'>;
  variant?: 'white' | 'dark' | 'green' | 'blue';
  iconSize?: 'small' | 'large';
  buttons?: ButtonProps[];
  items?: FeatureItemType[];
};

export interface ServiceCenterFeaturesProps extends ServiceCenterFeatures {
  className?: string;
  wrapperItemClassName?: string;
  tracking?: Pick<DataGAProps, 'eventName' | 'group'>;
}

export const ServiceCenterFeatures = ({
  className,
  wrapperItemClassName,
  variant = 'white',
  title,
  description,
  imageDesktop,
  imageMobile,
  iconSize = 'small',
  items = [],
  buttons = [],
  tracking,
  ...props
}: ServiceCenterFeaturesProps) => {
  useRevealAnim({ defaultBaseDelay: 0.04 });

  const textVariant = cn({
    'text-white': variant === 'dark',
    'text-green-500': variant === 'green',
    'text-azure-700': variant === 'blue',
  });

  return (
    <Section
      size="md"
      className={cn(
        'overflow-hidden',
        {
          'bg-linear-to-t from-gray-200 to-white': variant === 'white',
          'bg-linear-to-t from-gray-700 to-gray-700': variant === 'dark',
        },
        className
      )}
      {...createDataAttributes({ test: 'features-section' })}
      {...props}
      data-revealanim-group
    >
      {!!(imageDesktop?.src || imageMobile?.src) && (
        <div className="pointer-events-none absolute inset-0 opacity-15">
          <Image
            src={imageMobile?.src || imageDesktop?.src || ''}
            alt="Feature Banner"
            width={imageMobile?.width ?? 360}
            height={imageMobile?.height ?? 908}
            layout="fill"
            sources={generateSources('overall', {
              mobileSrc: imageMobile?.src,
              desktopSrc: imageDesktop?.src,
            })}
          />
        </div>
      )}

      <Container
        className="relative"
        {...createDataAttributes({ test: 'container' })}
      >
        {!!(title || description) && (
          <div
            className="where-span:text-inline-wrap mb-8 space-y-4 text-center xl:mb-16"
            {...createDataAttributes({ test: 'header' })}
            data-revealanim
          >
            {!!title && (
              <h2
                className={cn('text-sub-h1 font-noto-bold', textVariant)}
                dangerouslySetInnerHTML={{ __html: title }}
                {...createDataAttributes({ test: 'title' })}
                data-revealanim
              />
            )}
            {!!description && (
              <p
                className={cn(
                  'text-body-1 mx-auto max-w-80 md:max-w-154',
                  {
                    'text-gray-700': variant === 'white',
                  },
                  textVariant
                )}
                dangerouslySetInnerHTML={{ __html: description }}
                {...createDataAttributes({ test: 'description' })}
                data-revealanim
              />
            )}
          </div>
        )}

        {items.length > 0 && (
          <div
            className="mx-auto flex flex-wrap justify-center gap-x-6 gap-y-8 md:gap-x-10 lg:gap-x-20 lg:gap-y-10"
            {...createDataAttributes({ test: 'items' })}
            data-revealanim
          >
            {items.map((item, index) => (
              <div
                key={index}
                className={cn(
                  'flex max-w-40 flex-col items-center space-y-2.5 text-center',
                  styles.featuresItem,
                  wrapperItemClassName
                )}
                {...createDataAttributes({ test: `item-${index}` })}
              >
                {!!item.icon?.src && (
                  <div
                    className={cn(
                      'flex size-16 items-center justify-center rounded-full bg-white shadow-md lg:size-22'
                    )}
                    {...createDataAttributes({ test: 'icon' })}
                  >
                    <Image
                      src={item.icon.src}
                      alt={item.icon.alt || item.title || ''}
                      width={90}
                      height={90}
                      className={cn({
                        'max-w-10 xl:max-w-12': iconSize === 'small',
                        'max-w-12 xl:max-w-16': iconSize === 'large',
                      })}
                      aspectRatio="aspect-1-1"
                      sizes="(min-width: 1024px) 90px, 40px"
                    />
                  </div>
                )}

                {!!item.title && (
                  <p
                    className={cn(
                      'text-body-1 font-noto-bold where-span:text-inline-wrap',
                      textVariant
                    )}
                    dangerouslySetInnerHTML={{ __html: item.title }}
                    {...createDataAttributes({ test: 'item-title' })}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {buttons.length > 0 && (
          <ButtonGroup className="mt-10 justify-center" data-revealanim>
            {buttons.map((button, index) => (
              <Button
                key={index}
                {...button}
                className={cn('w-full md:w-auto', button.className)}
                {...createDataAttributes({ test: `button-${index}` })}
                {...createDataGA({
                  click: true,
                  url: button.href,
                  group: tracking?.group ?? 'service-center-features',
                })}
              />
            ))}
          </ButtonGroup>
        )}
      </Container>
    </Section>
  );
};
